import Button from "components/button";
import { VendorBranchFormValues, VendorBranchItem } from "_interfaces/vendor.interfaces";
import Icon from "components/icon";
import Input from "components/input";
import PhoneInput from "components/input/phone-input";
import Typography from "components/typography";
import { Controller, UseFormReturn } from "react-hook-form";
import { NamespaceKeys } from "locale/types";
import VendorAccordion from "./vendor-accordion";

interface VendorBranchAccordionProps {
  isOpen: boolean;
  onToggle: () => void;
  onNext: () => void;
  onAddToList: () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  branchItems: VendorBranchItem[];
  editingBranchIndex: number | null;
  form: UseFormReturn<VendorBranchFormValues>;
  t: (key: NamespaceKeys<"vendor">, options?: Record<string, unknown>) => string;
}

const VendorBranchAccordion = ({
  isOpen,
  onToggle,
  onNext,
  onAddToList,
  onEdit,
  onDelete,
  branchItems,
  editingBranchIndex,
  form,
  t,
}: VendorBranchAccordionProps) => {
  const {
    register,
    formState: { errors },
  } = form;

  return (
    <VendorAccordion
      title={t("accordion-vendor-branch")}
      subtitle={t("accordion-vendor-branch-subtitle")}
      iconName="building"
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-[1fr_1fr_auto] gap-4">
          <Input
            label={t("label-branch-name")}
            required
            placeholder={t("placeholder-branch-name")}
            data-testid="input-branch-name"
            {...register("branchName")}
            error={errors.branchName}
          />
          <Input
            label={t("label-pic-name")}
            required
            placeholder={t("placeholder-pic-name")}
            {...register("picName")}
            error={errors.picName}
          />
          <div />

          <Input
            label={t("label-pic-email")}
            required
            placeholder={t("placeholder-pic-email")}
            {...register("picEmail")}
            error={errors.picEmail}
          />
          <Controller
            name="picPhoneNumber"
            control={form.control}
            render={({ field, fieldState }) => (
              <PhoneInput
                label={t("label-pic-phone-number")}
                required
                value={field.value}
                onChange={(event) => field.onChange(event.target.value)}
                countryCode={form.watch("picCountryCode")}
                onCountryChange={(code) => form.setValue("picCountryCode", code)}
                placeholder={t("placeholder-pic-phone-number")}
                error={fieldState.error}
              />
            )}
          />
          <div className="flex items-end">
            <Button
              type="button"
              variant="fill"
              color="primary"
              onClick={onAddToList}
              data-testid="btn-add-branch"
            >
              {editingBranchIndex !== null ? t("btn-save-changes") : t("btn-add-to-list")}
            </Button>
          </div>
        </div>

        <div className="overflow-auto rounded-xl border border-border max-h-[330px]">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-greyScale-100 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left">{t("col-branch-name")}</th>
                <th className="px-4 py-3 text-left">{t("col-pic-name")}</th>
                <th className="px-4 py-3 text-left">{t("col-pic-email")}</th>
                <th className="px-4 py-3 text-left">{t("col-pic-phone")}</th>
                <th className="px-4 py-3 text-left">{t("col-action")}</th>
              </tr>
            </thead>
            <tbody>
              {branchItems.length === 0 ? (
                <tr>
                  <td className="px-4 py-8 text-center text-text-tertiary" colSpan={5}>
                    <Typography variant="body" tone="muted">
                      {t("empty-branch")}
                    </Typography>
                  </td>
                </tr>
              ) : (
                branchItems.map((item, index) => (
                  <tr key={`${item.branchName}-${index}`} className="border-t border-border">
                    <td className="px-4 py-3">{item.branchName}</td>
                    <td className="px-4 py-3">{item.picName}</td>
                    <td className="px-4 py-3">{item.picEmail}</td>
                    <td className="px-4 py-3">{`${item.picCountryCode} ${item.picPhoneNumber}`}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          color="tertiary"
                          size="small"
                          iconLeft={<Icon name="edit-2" size={14} />}
                          onClick={() => onEdit(index)}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          color="error"
                          size="small"
                          iconLeft={<Icon name="trash" size={14} />}
                          onClick={() => onDelete(index)}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end pt-1">
          <Button type="button" variant="outline" color="tertiary" onClick={onNext}>
            {t("btn-next")}
          </Button>
        </div>
      </div>
    </VendorAccordion>
  );
};

export default VendorBranchAccordion;
